import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'src/routes/hooks/use-router';
import { useRequestPasswordReset } from 'src/api/user';
import { enqueueSnackbar } from 'notistack';
import { paths } from 'src/routes/paths';

const PasswordResetRequestPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { requestPasswordReset } = useRequestPasswordReset();
  const router = useRouter();
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await requestPasswordReset(email);
      if (response.data.status === 'success') {
        enqueueSnackbar(t('password_reset.success'));
        setTimeout(() => router.push(paths.auth.jwt.login), 1000);
      } else {
        setError(response.data.message || t('password_reset.error'));
      }
    } catch (err) {
      setError(t('password_reset.error'));
      console.error('Error requesting password reset:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setError(null);
    setSuccess(null);
  };

  return (
    <>
      <Helmet>
        <title>{t('password_reset.title')}</title>
      </Helmet>
      <Container maxWidth="sm">
        <Box sx={{ my: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('password_reset.title')}
          </Typography>
          <Typography variant="body1" gutterBottom>
            {t('password_reset.description')}
          </Typography>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              margin="normal"
              label={t('password_reset.email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ mt: 3 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : t('password_reset.send')}
            </Button>
          </form>
        </Box>
      </Container>
      <Snackbar open={!!error || !!success} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={error ? "error" : "success"} sx={{ width: '100%' }}>
          {error || success}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PasswordResetRequestPage;
